import React from 'react';
import {
    Button,
    TextField,
    Typography,
} from '@mui/material';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import {
    useAppTheme,
    THEME_PRESETS,
    isValidHexColor,
    type ThemeShape,
    type ThemeTokens,
} from '@theme';
import './BrandingSettings.scss';

const TOKEN_FIELDS: { key: keyof ThemeTokens; label: string }[] = [
    { key: 'primary', label: 'Primary' },
    { key: 'secondary', label: 'Secondary' },
    { key: 'accent', label: 'Accent' },
    { key: 'background', label: 'Background' },
    { key: 'text', label: 'Text' },
    { key: 'textSecondary', label: 'Secondary text' },
];

const PRESET_LABELS: Record<string, string> = {
    default: 'Default',
    ocean: 'Ocean',
    sunset: 'Sunset',
    forest: 'Forest',
    lavender: 'Lavender',
    coral: 'Coral',
    midnight: 'Midnight',
    citrus: 'Citrus',
};

const BrandingSettings: React.FC = () => {
    const {
        tokens,
        setToken,
        applyPreset,
        resetBranding,
    } = useAppTheme();

    const handleColorChange = (key: keyof ThemeTokens, value: string) => {
        if (key === 'shape') {
            return;
        }
        const normalized = value.startsWith('#') ? value : `#${value}`;
        if (isValidHexColor(normalized)) {
            setToken(key, normalized);
        }
    };

    return (
        <div className="branding-settings">
            <section>
                <Typography className="branding-settings__section-title">
                    <PaletteRoundedIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '1.1rem' }} />
                    Theme presets
                </Typography>
                <Typography className="branding-settings__section-desc">
                    Start from a preset, then fine-tune individual colors below.
                </Typography>
                <div className="branding-settings__presets">
                    {Object.entries(THEME_PRESETS).map(([key, preset]) => (
                        <button
                            key={key}
                            type="button"
                            className="branding-settings__preset"
                            onClick={() => applyPreset(key as keyof typeof THEME_PRESETS)}
                        >
                            <span className="branding-settings__preset-dots">
                                <span className="branding-settings__preset-dot" style={{ background: preset.primary }} />
                                <span className="branding-settings__preset-dot" style={{ background: preset.secondary }} />
                                <span className="branding-settings__preset-dot" style={{ background: preset.accent }} />
                            </span>
                            {PRESET_LABELS[key] || key}
                        </button>
                    ))}
                </div>
            </section>

            <section>
                <Typography className="branding-settings__section-title">Colors</Typography>
                <Typography className="branding-settings__section-desc">
                    Changes apply instantly across the app and are saved on this device.
                </Typography>
                <div className="branding-settings__color-grid">
                    {TOKEN_FIELDS.map(({ key, label }) => (
                        <div className="branding-settings__color-field" key={key}>
                            <label className="branding-settings__color-label" htmlFor={`theme-${key}`}>
                                {label}
                            </label>
                            <div className="branding-settings__color-input-row">
                                <input
                                    id={`theme-${key}`}
                                    type="color"
                                    className="branding-settings__color-picker"
                                    value={tokens[key] as string}
                                    onChange={(e) => handleColorChange(key, e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={tokens[key] as string}
                                    onChange={(e) => handleColorChange(key, e.target.value)}
                                    inputProps={{ spellCheck: false }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <Typography className="branding-settings__section-title">Shape</Typography>
                <Typography className="branding-settings__section-desc">
                    Choose rounded corners for a softer look, or square for a sharper UI.
                </Typography>
                <div className="branding-settings__shape-toggle">
                    {(['rounded', 'square'] as ThemeShape[]).map((shape) => (
                        <button
                            key={shape}
                            type="button"
                            className={`branding-settings__shape-btn ${tokens.shape === shape ? 'branding-settings__shape-btn--active' : ''}`}
                            onClick={() => setToken('shape', shape)}
                        >
                            {shape === 'rounded' ? 'Rounded' : 'Square'}
                        </button>
                    ))}
                </div>
            </section>

            <section className="branding-settings__preview">
                <div className="branding-settings__preview-hero">
                    <p className="branding-settings__preview-title">Live preview</p>
                    <div className="branding-settings__preview-row">
                        <span className="branding-settings__preview-chip">Primary</span>
                        <span className="branding-settings__preview-chip">Accent</span>
                    </div>
                </div>
                <div className="branding-settings__preview-body">
                    <div className="branding-settings__preview-card">
                        <p className="branding-settings__preview-card-title">Buttons</p>
                        <div className="branding-settings__preview-row">
                            <span className="branding-settings__preview-btn">Primary</span>
                            <span className="branding-settings__preview-btn branding-settings__preview-btn--secondary">Secondary</span>
                            <span className="branding-settings__preview-btn branding-settings__preview-btn--accent">Accent</span>
                        </div>
                    </div>
                    <div className="branding-settings__preview-card">
                        <p className="branding-settings__preview-card-title">Typography</p>
                        <Typography sx={{ color: 'var(--text)', fontWeight: 700, fontSize: '0.9rem' }}>
                            Primary text sample
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Secondary text sample
                        </Typography>
                    </div>
                </div>
            </section>

            <div className="branding-settings__actions">
                <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={resetBranding}
                >
                    Reset to default
                </Button>
                <p className="branding-settings__saved-note">
                    Theme is auto-saved to this browser.
                </p>
            </div>
        </div>
    );
};

export default BrandingSettings;
